import { getConnectionInfo } from '@eluve/frontend-machine-utils';
import { CorrelatedLogger } from '@eluve/logger';

import type { CaptureProgress } from '../audio-sources/pcm-capture-stats';

export const MINIMUM_DATA_RATE_BYTES_PER_SECOND = 1000;
export const SECOND_IN_MS = 1000;
export const HEALTH_CHECK_INTERVAL_IN_MS = 15 * SECOND_IN_MS;
export const CONSECUTIVE_LOW_DATA_CHECKS_THRESHOLD = 2;
// Captured audio per wall-clock second must stay above this fraction for the
// window to count as flowing. Healthy capture sits at ~1.0; the slack absorbs
// hidden-tab message-delivery jitter (counters reach us via postMessage),
// while a stalled worklet reads ~0. [ENG-5045]
export const MINIMUM_CAPTURE_PROGRESS_RATIO = 0.5;
const DEFAULT_INTERVAL_DELAY_THRESHOLD_MULTIPLIER = 1.25;
const RECENT_CHECKS_HISTORY_LIMIT = 6;

type RecentCheckSnapshot = {
  timestamp: number;
  bytesDiff: number;
  dataRate: number;
  elapsedMs: number;
  isReceivingData: boolean;
  captureDeltaSeconds: number | null;
};

export class AudioHealthCheck {
  private interval: NodeJS.Timeout | null = null;
  private consecutiveLowDataChecks = 0;
  private readonly recentChecks: RecentCheckSnapshot[] = [];
  private readonly startedAt = Date.now();

  private healthCheckLastBytesReceived = 0;
  private healthCheckLastCapturedSeconds = 0;
  private healthCheckLastTime = Date.now();
  private readonly logger: CorrelatedLogger;
  private readonly healthCheckIntervalInMs;
  private readonly maxExpectedHealthCheckIntervalInMs;
  private readonly getTotalBytesReceived: () => number;
  private readonly getCaptureProgress?: () => CaptureProgress | null;
  private readonly getMediaStream: () => MediaStream | null;
  private readonly onHealthCheckFailed: (reason: string) => void;
  private readonly onHealthyAudioObserved?: () => void;
  private readonly getAdditionalLogData?: () => Record<string, unknown>;
  private readonly isHealthy: () =>
    | { healthy: true }
    | { healthy: false; reason: string };

  constructor({
    getTotalBytesReceived,
    getCaptureProgress,
    isHealthy,
    logger,
    healthCheckIntervalInMs = HEALTH_CHECK_INTERVAL_IN_MS,
    intervalDelayThresholdMultiplier = DEFAULT_INTERVAL_DELAY_THRESHOLD_MULTIPLIER,
    getAdditionalLogData,
    onHealthCheckFailed,
    onHealthyAudioObserved,
    getMediaStream,
  }: {
    getTotalBytesReceived: () => number;
    /**
     * Cumulative capture-side counters from the AudioWorklet, when the audio
     * source has them (v2 PCM stack). When provided and non-null, data flow is
     * judged from capture-counter deltas instead of the byte rate — encoded
     * bytes round-trip through the main thread, whose message delivery
     * browsers throttle in hidden tabs, while the worklet keeps capturing on
     * the unthrottled audio thread. Return null while no counters have
     * arrived (or omit entirely in blob/MediaRecorder mode, where delivery IS
     * the recording). [ENG-5045]
     */
    getCaptureProgress?: () => CaptureProgress | null;
    isHealthy: () => { healthy: true } | { healthy: false; reason: string };
    logger: CorrelatedLogger;
    healthCheckIntervalInMs?: number;
    intervalDelayThresholdMultiplier?: number;
    getAdditionalLogData?: () => Record<string, unknown>;
    onHealthCheckFailed: (reason: string) => void;
    /**
     * Called on each check where audio is genuinely flowing (healthy + data
     * above the minimum rate). Used to stamp a "last known good" timestamp so a
     * later device-sleep gap can be measured. [ENG-4498]
     */
    onHealthyAudioObserved?: () => void;
    getMediaStream: () => MediaStream | null;
  }) {
    this.getTotalBytesReceived = getTotalBytesReceived;
    this.getCaptureProgress = getCaptureProgress;
    this.isHealthy = isHealthy;
    this.logger = logger;
    this.healthCheckIntervalInMs = healthCheckIntervalInMs;
    this.getAdditionalLogData = getAdditionalLogData;
    this.onHealthCheckFailed = onHealthCheckFailed;
    this.onHealthyAudioObserved = onHealthyAudioObserved;
    this.getMediaStream = getMediaStream;
    this.maxExpectedHealthCheckIntervalInMs =
      healthCheckIntervalInMs * intervalDelayThresholdMultiplier;
  }

  start() {
    this.logger('info', 'Starting audio health check');
    this.interval = setInterval(() => {
      const connectionInfo = getConnectionInfo();
      const currentTime = Date.now();
      const elapsedMillisecondsSinceLastHealthCheck =
        currentTime - this.healthCheckLastTime;

      // Detect if browser suspended the tab/interval
      if (
        elapsedMillisecondsSinceLastHealthCheck >
        this.maxExpectedHealthCheckIntervalInMs
      ) {
        const percentageDelay =
          (elapsedMillisecondsSinceLastHealthCheck /
            this.healthCheckIntervalInMs -
            1) *
          100;

        this.logger(
          'warn',
          'Health check interval was delayed (browser suspension detected)',
          {
            elapsedMillisecondsSinceLastHealthCheck,
            expectedInterval: this.healthCheckIntervalInMs,
            maxExpectedInterval: this.maxExpectedHealthCheckIntervalInMs,
            percentageDelay,
          },
        );
      }

      const totalBytesReceived = this.getTotalBytesReceived();

      const bytesDiff = totalBytesReceived - this.healthCheckLastBytesReceived;
      const dataRate =
        bytesDiff / (elapsedMillisecondsSinceLastHealthCheck / 1000); // bytes per second

      // Expected data rate for audio should be much higher than this
      const minimumDataRate = MINIMUM_DATA_RATE_BYTES_PER_SECOND;
      const isDataRateTooLow = dataRate < minimumDataRate;

      const isReceivingData = totalBytesReceived > 0 && bytesDiff > 0;

      // Capture-side truth wins when available: the worklet's cumulative
      // counters advance in real time even when the browser batches message
      // delivery to a hidden tab, and read ~0 when capture genuinely stalls
      // (suspended AudioContext, starved input) — even if the encoder is
      // still draining a backlog of bytes. [ENG-5045]
      const captureProgress = this.getCaptureProgress?.() ?? null;
      let captureDeltaSeconds: number | null = null;
      let captureProgressRatio: number | null = null;
      if (captureProgress !== null) {
        captureDeltaSeconds =
          captureProgress.capturedSeconds - this.healthCheckLastCapturedSeconds;
        captureProgressRatio =
          captureDeltaSeconds /
          (elapsedMillisecondsSinceLastHealthCheck / 1000);
      }

      const isReceivingAudio =
        captureProgressRatio !== null
          ? captureProgressRatio >= MINIMUM_CAPTURE_PROGRESS_RATIO
          : isReceivingData && !isDataRateTooLow;

      const additionalLogData = this.getAdditionalLogData?.() ?? {};

      const mediaStream = this.getMediaStream();
      const tracks = mediaStream?.getAudioTracks().map((track) => {
        // Whitelist non-identifying settings. `MediaTrackSettings` can
        // expose `deviceId` and `groupId` (stable hardware identifiers);
        // those don't belong in centralized logs.
        const rawSettings =
          typeof track.getSettings === 'function' ? track.getSettings() : null;
        const settings = rawSettings
          ? {
              sampleRate: rawSettings.sampleRate,
              sampleSize: rawSettings.sampleSize,
              channelCount: rawSettings.channelCount,
              echoCancellation: rawSettings.echoCancellation,
              noiseSuppression: rawSettings.noiseSuppression,
              autoGainControl: rawSettings.autoGainControl,
            }
          : null;

        return {
          kind: track.kind,
          label: track.label,
          readyState: track.readyState,
          enabled: track.enabled,
          // `muted` is the browser-detected mute (OS preemption, BT profile
          // switch, exclusive-mode capture by another app). Distinct from
          // user-initiated mute, and the most common cause of a "live" track
          // that emits no data.
          muted: track.muted,
          settings,
        };
      });

      const documentRef =
        typeof document !== 'undefined' ? document : undefined;

      this.recentChecks.push({
        timestamp: currentTime,
        bytesDiff,
        dataRate,
        elapsedMs: elapsedMillisecondsSinceLastHealthCheck,
        isReceivingData,
        captureDeltaSeconds,
      });
      if (this.recentChecks.length > RECENT_CHECKS_HISTORY_LIMIT) {
        this.recentChecks.shift();
      }

      const commonLogData = {
        ...additionalLogData,
        hasMediaStream: Boolean(mediaStream),
        tracks,
        totalBytesReceived,
        elapsedMillisecondsSinceLastHealthCheck,
        dataRate,
        bytesDiff,
        minimumDataRate,
        isDataRateTooLow,
        isReceivingData,
        isReceivingAudio,
        captureProgress:
          captureProgress === null
            ? null
            : {
                ...captureProgress,
                deltaSeconds: captureDeltaSeconds,
                progressRatio: captureProgressRatio,
                minimumProgressRatio: MINIMUM_CAPTURE_PROGRESS_RATIO,
              },
        consecutiveLowDataChecks: this.consecutiveLowDataChecks,
        connectionDownlink: connectionInfo?.downlink,
        connectionEffectiveType: connectionInfo?.effectiveType,
        documentVisibility: documentRef?.visibilityState,
        documentHidden: documentRef?.hidden,
        msSinceHealthCheckStart: currentTime - this.startedAt,
        recentChecks: this.recentChecks.slice(),
      };

      const healthResult = this.isHealthy();
      if (healthResult.healthy === false) {
        this.logger('warn', 'Audio health check failed', {
          errorMessage: healthResult.reason,
          ...commonLogData,
        });

        this.onHealthCheckFailed(healthResult.reason);
      } else if (!isReceivingAudio) {
        this.consecutiveLowDataChecks++;
        commonLogData.consecutiveLowDataChecks = this.consecutiveLowDataChecks;

        if (
          this.consecutiveLowDataChecks >= CONSECUTIVE_LOW_DATA_CHECKS_THRESHOLD
        ) {
          const reason = `No audio data flow for ${this.consecutiveLowDataChecks} consecutive checks`;
          this.logger(
            'warn',
            'Audio health check failed: sustained data flow loss',
            commonLogData,
          );
          this.logAvailableInputDevices(reason);
          this.onHealthCheckFailed(reason);
        } else {
          this.logger(
            'warn',
            'Audio health check warning: no audio data flow detected',
            commonLogData,
          );
        }
      } else {
        this.consecutiveLowDataChecks = 0;
        commonLogData.consecutiveLowDataChecks = 0;
        this.logger('info', 'Audio health check passed', commonLogData);
        // Audio is genuinely flowing right now — stamp this as the last known
        // good moment so a subsequent device-sleep gap can be measured. [ENG-4498]
        this.onHealthyAudioObserved?.();
      }

      this.healthCheckLastBytesReceived = totalBytesReceived;
      if (captureProgress !== null) {
        this.healthCheckLastCapturedSeconds = captureProgress.capturedSeconds;
      }
      this.healthCheckLastTime = currentTime;
    }, this.healthCheckIntervalInMs);
  }

  stop() {
    this.logger('info', 'Stopping audio health check');
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    this.consecutiveLowDataChecks = 0;
  }

  private logAvailableInputDevices(reason: string) {
    const navigatorRef =
      typeof navigator !== 'undefined' ? navigator : undefined;
    const enumerate = navigatorRef?.mediaDevices?.enumerateDevices?.bind(
      navigatorRef.mediaDevices,
    );
    if (!enumerate) return;

    enumerate()
      .then((devices) => {
        // Intentionally omit raw `deviceId` / `groupId` — those are stable
        // browser identifiers and don't belong in centralized logs. Labels
        // are user-meaningful ("MacBook Air Microphone") and sufficient for
        // diagnosing whether the expected input is present.
        const audioInputLabels = devices
          .filter((d) => d.kind === 'audioinput')
          .map((d) => d.label);
        this.logger(
          'warn',
          'Audio health check post-failure device inventory',
          {
            reason,
            audioInputCount: audioInputLabels.length,
            audioInputLabels,
          },
        );
      })
      .catch((error) => {
        this.logger(
          'warn',
          'Audio health check post-failure device enumeration failed',
          {
            reason,
            error: error instanceof Error ? error.message : String(error),
          },
        );
      });
  }
}
